import { useCallback, useEffect, useState } from 'react'
import { useAccount, useConfig, useConnect, useWriteContract } from 'wagmi'
import { readContract, waitForTransactionReceipt, switchChain, getChainId } from 'wagmi/actions'
import { formatUnits } from 'viem'
import {
  OSE_TOKEN_ADDRESS,
  ARCADE_ADDRESS,
  ARCADE_CHAIN,
  ARCADE_ABI,
  ERC20_ABI,
  DEFAULT_COST,
  gameIdBytes32,
} from '../contract'

// Approve enough for several plays at once so returning players only sign
// the insertCoin transaction.
const APPROVE_PLAYS = 10n

function fmtOSE(wei) {
  return Math.floor(Number(formatUnits(wei, 18))).toLocaleString()
}

function friendlyError(e) {
  const msg = e?.shortMessage || e?.message || String(e)
  if (/user rejected|denied|cancell?ed/i.test(msg)) return 'TRANSACTION CANCELLED'
  if (/insufficient funds/i.test(msg)) return 'NOT ENOUGH ETH ON BASE FOR GAS'
  if (/InsufficientBalance/i.test(msg)) return 'NOT ENOUGH OSE TO PLAY'
  if (/InsufficientAllowance/i.test(msg)) return 'APPROVAL TOO LOW — TRY AGAIN'
  return msg.slice(0, 120).toUpperCase()
}

/**
 * Coin slot screen shown before a game mounts. One play burns
 * `costPerTurn` OSE (10,000) from the player's wallet through the
 * OSEArcade contract. Calls `onSuccess()` once the burn is confirmed.
 */
export default function InsertCoin({ game, inMiniApp, onSuccess }) {
  const config = useConfig()
  const { address, isConnected } = useAccount()
  const { connect, connectors, isPending: connecting } = useConnect()
  const { writeContractAsync } = useWriteContract()

  const [phase, setPhase] = useState('checking') // checking | idle | approving | inserting
  const [status, setStatus] = useState(null) // { balance, allowance, cost }
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    if (!address || !ARCADE_ADDRESS) return
    setPhase('checking')
    setError(null)
    try {
      const [balance, allowance, cost] = await Promise.all([
        readContract(config, {
          chainId: ARCADE_CHAIN.id,
          address: OSE_TOKEN_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [address],
        }),
        readContract(config, {
          chainId: ARCADE_CHAIN.id,
          address: OSE_TOKEN_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'allowance',
          args: [address, ARCADE_ADDRESS],
        }),
        readContract(config, {
          chainId: ARCADE_CHAIN.id,
          address: ARCADE_ADDRESS,
          abi: ARCADE_ABI,
          functionName: 'costPerTurn',
        }).catch(() => DEFAULT_COST),
      ])
      setStatus({ balance, allowance, cost })
      setPhase('idle')
    } catch {
      setError('COULD NOT READ OSE BALANCE — CHECK CONNECTION')
      setStatus(null)
      setPhase('idle')
    }
  }, [address, config, game.id])

  useEffect(() => {
    refresh()
  }, [refresh])

  const insert = async () => {
    if (!status) {
      refresh()
      return
    }
    setError(null)
    const { balance, allowance, cost } = status
    if (balance < cost) {
      setError(`NOT ENOUGH OSE — YOU HAVE ${fmtOSE(balance)}, NEED ${fmtOSE(cost)}`)
      return
    }
    try {
      // The OSE token lives on Base — make sure the wallet is there.
      if (getChainId(config) !== ARCADE_CHAIN.id) {
        try {
          await switchChain(config, { chainId: ARCADE_CHAIN.id })
        } catch {
          /* continue — the tx carries an explicit chainId */
        }
      }

      if (allowance < cost) {
        setPhase('approving')
        const approveHash = await writeContractAsync({
          chainId: ARCADE_CHAIN.id,
          address: OSE_TOKEN_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [ARCADE_ADDRESS, cost * APPROVE_PLAYS],
        })
        await waitForTransactionReceipt(config, { chainId: ARCADE_CHAIN.id, hash: approveHash })
      }

      setPhase('inserting')
      const hash = await writeContractAsync({
        chainId: ARCADE_CHAIN.id,
        address: ARCADE_ADDRESS,
        abi: ARCADE_ABI,
        functionName: 'insertCoin',
        args: [gameIdBytes32(game.id)],
      })
      const receipt = await waitForTransactionReceipt(config, {
        chainId: ARCADE_CHAIN.id,
        hash,
      })
      if (receipt.status !== 'success') throw new Error('TRANSACTION REVERTED')
      onSuccess()
    } catch (e) {
      setError(friendlyError(e))
      setPhase('idle')
      refresh()
    }
  }

  const busy = phase === 'approving' || phase === 'inserting'

  return (
    <div className="go-panel coin-panel" style={{ '--accent': game.accent }}>
      <p className="go-score">INSERT COIN</p>
      <p className="go-status">
        1 PLAY = {status ? fmtOSE(status.cost) : '10,000'} OSE (BURNED)
        {status && (
          <>
            <br />
            YOUR BALANCE: {fmtOSE(status.balance)} OSE
          </>
        )}
      </p>

      {!ARCADE_ADDRESS ? (
        <p className="go-status coin-error">
          ARCADE CONTRACT NOT CONFIGURED
          <br />
          SET VITE_ARCADE_ADDRESS AND REDEPLOY
        </p>
      ) : !isConnected ? (
        <>
          <p className="go-status">
            {inMiniApp ? 'CONNECT YOUR WALLET TO PLAY' : 'OPEN IN FARCASTER TO PLAY'}
          </p>
          {connectors.length > 0 && (
            <button
              type="button"
              className="go-lb-link"
              disabled={connecting}
              onClick={() => connect({ connector: connectors[0] })}
            >
              {connecting ? 'CONNECTING…' : '🔌 CONNECT WALLET'}
            </button>
          )}
        </>
      ) : (
        <>
          {error && <p className="go-status coin-error">{error}</p>}
          {status && status.balance < status.cost ? (
            <p className="go-status coin-error">
              NOT ENOUGH OSE TO PLAY
              <br />
              NEED {fmtOSE(status.cost)} · HAVE {fmtOSE(status.balance)}
            </p>
          ) : (
            <button type="button" className="go-lb-link" disabled={busy || phase === 'checking'} onClick={insert}>
              {phase === 'checking' && 'CHECKING BALANCE…'}
              {phase === 'idle' && '🪙 INSERT COIN'}
              {phase === 'approving' && 'APPROVING OSE… CONFIRM IN WALLET'}
              {phase === 'inserting' && 'INSERTING COIN… PLEASE WAIT'}
            </button>
          )}
          {status && status.allowance < status.cost && phase === 'idle' && (
            <p className="go-status">FIRST PLAY ASKS FOR A ONE-TIME APPROVAL</p>
          )}
        </>
      )}

      {import.meta.env.DEV && (
        <button type="button" className="back-btn" onClick={onSuccess}>
          FREE PLAY (DEV BUILD ONLY)
        </button>
      )}
    </div>
  )
}
